import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSecureAuthContext } from '@/contexts/SecureAuthContext';

export interface ABTestVariant {
  id: string;
  session_id: string;
  image_id: string | null;
  variant_name: string;
  variant_config: any;
  votes: number;
  client_votes: number;
  internal_notes: string | null;
  is_winner: boolean;
  performance_score: number | null;
  created_at: string;
  updated_at: string;
}

export const useABTestVariants = (sessionId: string | null) => {
  const { user } = useSecureAuthContext();
  const [variants, setVariants] = useState<ABTestVariant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const channelRef = useRef<any>(null);

  const fetchVariants = async () => {
    if (!sessionId || !user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('ab_test_variants')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });

      if (fetchError) throw fetchError;
      setVariants(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching variants:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch variants');
    } finally {
      setLoading(false);
    }
  };

  const createVariant = async (variantData: Omit<ABTestVariant, 'id' | 'votes' | 'client_votes' | 'is_winner' | 'performance_score' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error: createError } = await supabase
        .from('ab_test_variants')
        .insert(variantData)
        .select()
        .single();

      if (createError) throw createError;
      
      setVariants(prev => [...prev, data]);
      return { data, error: null };
    } catch (err) {
      console.error('Error creating variant:', err);
      return { data: null, error: err instanceof Error ? err.message : 'Failed to create variant' };
    }
  };

  const castVote = async (variantId: string, rating?: number, comment?: string) => {
    if (!user) throw new Error('User not authenticated');

    try {
      // Insert vote
      const { error: voteError } = await supabase
        .from('ab_test_votes')
        .insert({
          variant_id: variantId,
          session_id: sessionId!,
          voter_type: 'owner',
          voter_email: user.email,
          rating,
          comment
        });

      if (voteError) throw voteError;

      // Increment variant votes (get current votes and increment)
      const { data: currentVariant } = await supabase
        .from('ab_test_variants')
        .select('votes')
        .eq('id', variantId)
        .single();

      const { error: updateError } = await supabase
        .from('ab_test_variants')
        .update({ votes: (currentVariant?.votes || 0) + 1 })
        .eq('id', variantId);

      if (updateError) throw updateError;

      // Refresh variants
      await fetchVariants();
      return { error: null };
    } catch (err) {
      console.error('Error casting vote:', err);
      return { error: err instanceof Error ? err.message : 'Failed to cast vote' };
    }
  };

  const setWinner = async (variantId: string) => {
    if (!sessionId) return { error: 'No session ID provided' };

    try {
      // Reset all variants is_winner to false
      await supabase
        .from('ab_test_variants')
        .update({ is_winner: false })
        .eq('session_id', sessionId);

      // Set the winner
      const { error: variantError } = await supabase
        .from('ab_test_variants')
        .update({ is_winner: true })
        .eq('id', variantId);

      if (variantError) throw variantError;

      // Update session with winner
      const { error: sessionError } = await supabase
        .from('ab_test_sessions')
        .update({
          winner_variant_id: variantId,
          status: 'completed',
          completed_at: new Date().toISOString()
        })
        .eq('id', sessionId);

      if (sessionError) throw sessionError;

      await fetchVariants();
      return { error: null };
    } catch (err) {
      console.error('Error setting winner:', err);
      return { error: err instanceof Error ? err.message : 'Failed to set winner' };
    }
  };

  useEffect(() => {
    fetchVariants();

    if (!sessionId || !user) return;

    // Clean up any existing channel (handles React Strict Mode double effects)
    if (channelRef.current) {
      try {
        supabase.removeChannel(channelRef.current);
      } catch (e) {
        console.warn('Failed removing previous ab-variants channel', e);
      }
      channelRef.current = null;
    }

    // Create a unique channel name to avoid reusing the same instance
    const channelName = `ab-variants-${sessionId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const channel = supabase.channel(channelName);
    channelRef.current = channel;

    channel.on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'ab_test_variants',
      filter: `session_id=eq.${sessionId}`
    }, () => {
      fetchVariants();
    });

    channel.subscribe();

    return () => {
      if (channelRef.current) {
        try {
          supabase.removeChannel(channelRef.current);
        } catch (e) {
          console.warn('Error removing ab-variants channel', e);
        }
        channelRef.current = null;
      }
    };
  }, [sessionId, user]);

  const deleteVariant = async (variantId: string) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const { error: deleteError } = await supabase
        .from('ab_test_variants')
        .delete()
        .eq('id', variantId);

      if (deleteError) throw deleteError;

      // Update local state immediately
      setVariants(prev => prev.filter(v => v.id !== variantId));
      
      return { error: null };
    } catch (err) {
      console.error('Error deleting variant:', err);
      return { error: err instanceof Error ? err.message : 'Failed to delete variant' };
    }
  };

  return {
    variants,
    loading,
    error,
    createVariant,
    castVote,
    setWinner,
    deleteVariant,
    refetch: fetchVariants
  };
};
